/**
 * Tab Pendências - Página com KPIs e tabela de unidades pendentes em Alvará de Funcionamento e AVCB
 * 
 * Critério de Regularização:
 * - Considera apenas documentos de "Alvará de Funcionamento" e "AVCB"
 * - Unidade REGULAR = TODOS os documentos (Alvará + AVCB) devem estar Regular
 * - Unidade IRREGULAR = Qualquer documento não-Regular
 */
import { usePendencias, useRegionals, useStatusHistory, useMultipleProcessComments, PendenciaUnit, StatusChangeRecord, IrregularDoc, ProcessCommentWithSource } from './hooks';
import { AlertCircle, Building2, CheckCircle, Clock, AlertTriangle, FileX2, Search, Download, Filter, History, Loader2, FileCheck, FileMinus, MessageSquare, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import * as XLSX from 'xlsx';
import { ExcelPreviewDialog, type PreviewSheet } from '@/components/ExcelPreviewDialog';

// Formata CNPJ: 00.000.000/0000-00
function formatCNPJ(cnpj: string): string {
  if (!cnpj) return '-';
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

// KPI Card
interface KpiCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color?: 'default' | 'green' | 'yellow' | 'red' | 'gray';
  isLoading?: boolean;
  className?: string;
  onClick?: () => void;
  isActive?: boolean;
  subtitle?: string;
}

function KpiCard({ title, value, icon, color = 'default', isLoading, className, onClick, isActive, subtitle }: KpiCardProps) {
  const colorClasses = {
    default: '',
    green: 'border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20',
    yellow: 'border-yellow-500/30 bg-yellow-50/50 dark:bg-yellow-950/20',
    red: 'border-red-500/30 bg-red-50/50 dark:bg-red-950/20',
    gray: 'border-gray-500/30 bg-gray-50/50 dark:bg-gray-950/20',
  };

  if (isLoading) {
    return (
      <div className={cn('bg-card rounded-lg border p-4 animate-pulse', className)}>
        <div className="h-4 bg-muted rounded w-24 mb-2" />
        <div className="h-8 bg-muted rounded w-16" />
      </div>
    );
  }

  return (
    <div 
      className={cn(
        'bg-card rounded-lg border p-4', 
        colorClasses[color], 
        onClick && 'cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all',
        isActive && 'ring-2 ring-primary',
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="text-3xl font-bold">{value.toLocaleString('pt-BR')}</div>
      {subtitle && (
        <p className="text-[11px] text-muted-foreground mt-1">{subtitle}</p>
      )}
    </div>
  );
}

// Status Badge - atualizado para nova estrutura
function StatusBadge({ status }: { status: 'em_andamento' | 'irregular' | 'sem_doc' }) {
  const config = {
    em_andamento: { label: 'Em Andamento', variant: 'andamento' as const, icon: Clock },
    irregular: { label: 'Irregular', variant: 'irregular' as const, icon: AlertTriangle },
    sem_doc: { label: 'Sem Documento', variant: 'secondary' as const, icon: FileX2 },
  };

  const { label, variant, icon: Icon } = config[status];

  return (
    <Badge variant={variant} className="gap-1">
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

// Badge para indicar status do documento (Alvará ou AVCB)
// Verifica se está regular, em andamento, irregular ou não cadastrado
function DocStatusBadge({ 
  hasDoc, 
  docType, 
  documentosIrregulares 
}: { 
  hasDoc: boolean; 
  docType: 'Alvará' | 'AVCB';
  documentosIrregulares: IrregularDoc[];
}) {
  // Busca documentos irregulares deste tipo
  const docsIrregulares = documentosIrregulares.filter(d => d.tipo === docType);
  
  // Se não tem documento cadastrado
  if (!hasDoc) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge variant="outline" className="gap-1 bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800">
              <FileMinus className="h-3 w-3" />
              {docType}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>Documento de {docType} NÃO cadastrado</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  // Se tem documento mas está irregular
  if (docsIrregulares.length > 0) {
    const hasIrregular = docsIrregulares.some(d => d.categoria === 'Irregular');
    const hasEmAndamento = docsIrregulares.some(d => d.categoria === 'Em Andamento');
    
    // Irregular (vermelho)
    if (hasIrregular) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="outline" className="gap-1 bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800">
                <AlertTriangle className="h-3 w-3" />
                {docType}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <div className="space-y-1">
                <p className="font-medium text-red-600">Irregular</p>
                {docsIrregulares.map((d, i) => (
                  <p key={i} className="text-sm">{d.status}</p>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    
    // Em Andamento (amarelo)
    if (hasEmAndamento) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="outline" className="gap-1 bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-300 dark:border-yellow-800">
                <Clock className="h-3 w-3" />
                {docType}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <div className="space-y-1">
                <p className="font-medium text-yellow-600">Em Andamento</p>
                {docsIrregulares.map((d, i) => (
                  <p key={i} className="text-sm">{d.status}</p>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
  }
  
  // Documento existe e está regular (verde)
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge variant="outline" className="gap-1 bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800">
            <FileCheck className="h-3 w-3" />
            {docType}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-emerald-600 font-medium">Regular</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Tipos para filtros de KPI
type DocTypeFilter = 'all' | 'alvara' | 'avcb';
type KpiFilter = 'all' | 'regular' | 'pendente' | 'em_andamento' | 'irregular' | 'sem_doc';

export function PendenciasTab() {
  // Estados de filtro
  const [searchInput, setSearchInput] = useState(''); // Input controlado
  const [searchTerm, setSearchTerm] = useState(''); // Termo com debounce
  const [selectedRegional, setSelectedRegional] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]); // Multi-select
  const [selectedFornecedor, setSelectedFornecedor] = useState<string[]>([]); // Multi-select
  
  // Filtros de KPI (tipo de documento e status)
  const [docTypeFilter, setDocTypeFilter] = useState<DocTypeFilter>('alvara');
  const [kpiFilter, setKpiFilter] = useState<KpiFilter>('all');

  // Preview
  const [showPreview, setShowPreview] = useState(false);
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50; // Mostra 50 itens por página
  
  // Estado para histórico de status
  const [selectedUnit, setSelectedUnit] = useState<PendenciaUnit | null>(null);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  // Estado para dialog de detalhes de pendências
  const [detailUnit, setDetailUnit] = useState<PendenciaUnit | null>(null);

  // Debounce do campo de busca (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Hooks de dados
  const { data: regionals, isLoading: loadingRegionals } = useRegionals();
  const { data, isLoading } = usePendencias(
    selectedRegional !== 'all' ? { regional: selectedRegional } : undefined
  );
  
  // Hook para histórico de status da unidade selecionada
  const { data: statusHistory, isLoading: loadingHistory } = useStatusHistory(
    selectedUnit?.UnitName || null,
    historyDrawerOpen
  );

  // Pega os processGuids por tipo de documento (agora vem direto da unidade como arrays)
  const processGuidsByDocType = useMemo(() => {
    if (!selectedUnit) return { alvara: [], avcb: [] };
    
    return {
      alvara: selectedUnit.alvaraProcessGuids || [],
      avcb: selectedUnit.avcbProcessGuids || [],
    };
  }, [selectedUnit]);

  // Estado para o tipo de documento selecionado nos comentários
  const [selectedDocType, setSelectedDocType] = useState<'alvara' | 'avcb'>('alvara');

  // ProcessGuids baseado no tipo selecionado (agora é array)
  const activeProcessGuids = processGuidsByDocType[selectedDocType];

  // Hook para comentários de TODOS os processos do tipo selecionado
  const { data: processComments, isLoading: loadingComments } = useMultipleProcessComments(
    activeProcessGuids,
    historyDrawerOpen && activeProcessGuids.length > 0
  );

  // Estado para controlar a aba ativa no drawer
  const [activeTab, setActiveTab] = useState<'historico' | 'comentarios'>('historico');

  // Reset da aba e tipo de documento quando fechar o drawer
  useEffect(() => {
    if (!historyDrawerOpen) {
      setActiveTab('historico');
      setSelectedDocType('alvara');
    }
  }, [historyDrawerOpen]);

  // Abre drawer de histórico
  const handleViewHistory = (unit: PendenciaUnit) => {
    setSelectedUnit(unit);
    setHistoryDrawerOpen(true);
  };

  // Helper para determinar o status de um documento específico (Alvará ou AVCB) para uma unidade
  const getDocTypeStatus = useCallback((unit: PendenciaUnit, docType: 'alvara' | 'avcb'): 'regular' | 'em_andamento' | 'irregular' | 'sem_doc' => {
    const tipoDoc = docType === 'alvara' ? 'Alvará' : 'AVCB';
    const temDoc = docType === 'alvara' ? unit.temAlvara : unit.temAvcb;
    
    if (!temDoc) return 'sem_doc';
    
    // Verifica se há documentos irregulares deste tipo
    const docsIrreg = unit.documentosIrregulares.filter(d => d.tipo === tipoDoc);
    if (docsIrreg.length === 0) return 'regular';
    
    // Se tem apenas "Em Andamento", retorna em_andamento, senão irregular
    const hasIrregular = docsIrreg.some(d => d.categoria === 'Irregular');
    return hasIrregular ? 'irregular' : 'em_andamento';
  }, []);

  // Filtra unidades
  const filteredUnits = useMemo(() => {
    if (!data?.units) return [];
    
    // Obtém TODAS as unidades (regulares + pendentes) para filtro completo
    // Mas a API só retorna pendentes, então precisamos tratar isso
    let allUnitsToFilter = data.units;
    
    return allUnitsToFilter.filter(unit => {
      // Filtro por tipo de documento (Alvará/AVCB) - verifica se a unidade tem pendência nesse tipo
      const docStatus = getDocTypeStatus(unit, docTypeFilter === 'all' ? 'alvara' : docTypeFilter);
      
      // Filtro por KPI selecionado
      if (kpiFilter !== 'all') {
        if (kpiFilter === 'regular' && docStatus !== 'regular') return false;
        if (kpiFilter === 'pendente' && docStatus === 'regular') return false;
        if (kpiFilter === 'em_andamento' && docStatus !== 'em_andamento') return false;
        if (kpiFilter === 'irregular' && docStatus !== 'irregular') return false;
        if (kpiFilter === 'sem_doc' && docStatus !== 'sem_doc') return false;
      } else {
        // Se não tem filtro de KPI, mostra apenas as pendentes nesse tipo
        if (docStatus === 'regular') return false;
      }
      
      // Filtro por busca — pesquisa em TODOS os campos disponíveis da unidade
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const searchableFields = [
          unit.UnitName,
          unit.cnpj,
          unit.endereco,
          unit.RegionalName,
          unit.CityStateName,
          unit.UF,
          unit.fornecedor,
          unit.motivoResumo,
          String(unit.UnitId),
          unit.UnitGuid,
        ];
        const matchesField = searchableFields.some(f => f?.toLowerCase().includes(search));
        // Busca também nos motivos (PendencyTypeName) dos documentos irregulares
        const matchesPendencyType = unit.documentosIrregulares.some(
          d => d.motivo?.toLowerCase().includes(search)
        );
        if (!matchesField && !matchesPendencyType) return false;
      }

      // Filtro por status (multi-select)
      if (selectedStatus.length > 0 && !selectedStatus.includes(unit.status)) {
        return false;
      }

      // Filtro por fornecedor (multi-select)
      if (selectedFornecedor.length > 0 && !selectedFornecedor.includes(unit.fornecedor || '')) {
        return false;
      }

      return true;
    });
  }, [data?.units, searchTerm, selectedStatus, selectedFornecedor, docTypeFilter, kpiFilter, getDocTypeStatus]);

  // Paginação dos resultados
  const totalPages = Math.ceil(filteredUnits.length / pageSize);
  const paginatedUnits = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredUnits.slice(start, start + pageSize);
  }, [filteredUnits, currentPage, pageSize]);

  // Reset página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatus, selectedFornecedor, selectedRegional, docTypeFilter, kpiFilter]);

  // Extrai lista única de fornecedores para o filtro
  const fornecedores = useMemo(() => {
    if (!data?.units) return [];
    const uniqueFornecedores = [...new Set(data.units.map(u => u.fornecedor).filter(Boolean))];
    return uniqueFornecedores.sort();
  }, [data?.units]);

  // Exporta para XLSX
  const handleExportXLSX = () => {
    if (!filteredUnits.length) return;

    // --- Aba 1: Resumo por unidade ---
    const resumoHeaders = ['Unidade', 'CNPJ', 'Endereço', 'Regional', 'UF', 'Tem Alvará', 'Tem AVCB', 'Motivo Resumo', 'Motivo Detalhado', 'Fornecedor', 'Status', 'Documentos Irregulares'];
    const resumoRows = filteredUnits.map(unit => {
      // Motivos detalhados: tipo + docName + motivo (PendencyTypeName) + status
      const motivoDetalhado = unit.documentosIrregulares
        .map(d => {
          const parts: string[] = [d.tipo];
          if (d.docName) parts.push(d.docName);
          if (d.motivo) parts.push(`Motivo: ${d.motivo}`);
          parts.push(`Status: ${d.status}`);
          return parts.join(' — ');
        })
        .join(' | ') || '-';

      return {
        'Unidade': unit.UnitName || '',
        'CNPJ': formatCNPJ(unit.cnpj),
        'Endereço': unit.endereco || '',
        'Regional': unit.RegionalName || '',
        'UF': unit.UF || '',
        'Tem Alvará': unit.temAlvara ? 'Sim' : 'Não',
        'Tem AVCB': unit.temAvcb ? 'Sim' : 'Não',
        'Motivo Resumo': unit.motivoResumo || '',
        'Motivo Detalhado': motivoDetalhado,
        'Fornecedor': unit.fornecedor || '',
        'Status': unit.status === 'em_andamento' ? 'Em Andamento' : unit.status === 'irregular' ? 'Irregular' : 'Sem Documento',
        'Documentos Irregulares': unit.documentosIrregulares.map(d => `${d.tipo}: ${d.status}`).join('; '),
      };
    });

    const resumoSheet = XLSX.utils.json_to_sheet(resumoRows);
    const resumoColWidths = resumoHeaders.map((h, i) => ({
      wch: Math.min(60, Math.max(h.length, ...resumoRows.map(r => String(Object.values(r)[i] || '').length).slice(0, 100)) + 2)
    }));
    resumoSheet['!cols'] = resumoColWidths;

    // --- Aba 2: Pendências detalhadas (uma linha por documento irregular) ---
    const detalheHeaders = ['Unidade', 'CNPJ', 'Endereço', 'Regional', 'UF', 'Documento', 'Tipo', 'Identificação Doc', 'Categoria', 'Status Documento', 'Motivo (Pendência)', 'Fornecedor Doc', 'Vencimento'];
    const detalheRows: Record<string, string>[] = [];
    filteredUnits.forEach(unit => {
      if (unit.documentosIrregulares.length === 0) {
        // Unidade sem docs — uma linha só com dados da unidade
        detalheRows.push({
          'Unidade': unit.UnitName || '',
          'CNPJ': formatCNPJ(unit.cnpj),
          'Endereço': unit.endereco || '',
          'Regional': unit.RegionalName || '',
          'UF': unit.UF || '',
          'Documento': '-',
          'Tipo': '-',
          'Identificação Doc': '-',
          'Categoria': '-',
          'Status Documento': '-',
          'Motivo (Pendência)': '-',
          'Fornecedor Doc': '-',
          'Vencimento': '-',
        });
      } else {
        unit.documentosIrregulares.forEach(d => {
          detalheRows.push({
            'Unidade': unit.UnitName || '',
            'CNPJ': formatCNPJ(unit.cnpj),
            'Endereço': unit.endereco || '',
            'Regional': unit.RegionalName || '',
            'UF': unit.UF || '',
            'Documento': d.tipoCompleto || d.tipo,
            'Tipo': d.tipo,
            'Identificação Doc': d.docName || '-',
            'Categoria': d.categoria,
            'Status Documento': d.status,
            'Motivo (Pendência)': d.motivo || '-',
            'Fornecedor Doc': d.fornecedor || '-',
            'Vencimento': d.vencimento || '-',
          });
        });
      }
    });

    const detalheSheet = XLSX.utils.json_to_sheet(detalheRows);
    const detalheColWidths = detalheHeaders.map((h, i) => ({
      wch: Math.min(60, Math.max(h.length, ...detalheRows.map(r => String(Object.values(r)[i] || '').length).slice(0, 100)) + 2)
    }));
    detalheSheet['!cols'] = detalheColWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, resumoSheet, 'Resumo');
    XLSX.utils.book_append_sheet(workbook, detalheSheet, 'Pendências Detalhadas');
    
    XLSX.writeFile(workbook, `pendencias-alvara-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Preview sheets (same data as export)
  const previewSheets = useMemo<PreviewSheet[]>(() => {
    if (!filteredUnits.length) return [];
    const RESUMO_HEADERS = ['Unidade', 'CNPJ', 'Endereço', 'Regional', 'UF', 'Tem Alvará', 'Tem AVCB', 'Motivo Resumo', 'Motivo Detalhado', 'Fornecedor', 'Status', 'Documentos Irregulares'];
    const resumoRows = filteredUnits.map(unit => {
      const motivoDetalhado = unit.documentosIrregulares
        .map(d => {
          const parts: string[] = [d.tipo];
          if (d.docName) parts.push(d.docName);
          if (d.motivo) parts.push(`Motivo: ${d.motivo}`);
          parts.push(`Status: ${d.status}`);
          return parts.join(' — ');
        })
        .join(' | ') || '-';
      return {
        'Unidade': unit.UnitName || '',
        'CNPJ': formatCNPJ(unit.cnpj),
        'Endereço': unit.endereco || '',
        'Regional': unit.RegionalName || '',
        'UF': unit.UF || '',
        'Tem Alvará': unit.temAlvara ? 'Sim' : 'Não',
        'Tem AVCB': unit.temAvcb ? 'Sim' : 'Não',
        'Motivo Resumo': unit.motivoResumo || '',
        'Motivo Detalhado': motivoDetalhado,
        'Fornecedor': unit.fornecedor || '',
        'Status': unit.status === 'em_andamento' ? 'Em Andamento' : unit.status === 'irregular' ? 'Irregular' : 'Sem Documento',
        'Documentos Irregulares': unit.documentosIrregulares.map(d => `${d.tipo}: ${d.status}`).join('; '),
      };
    });

    const DETALHE_HEADERS = ['Unidade', 'CNPJ', 'Endereço', 'Regional', 'UF', 'Documento', 'Tipo', 'Identificação Doc', 'Categoria', 'Status Documento', 'Motivo (Pendência)', 'Fornecedor Doc', 'Vencimento'];
    const detalheRows: Record<string, string | number>[] = [];
    filteredUnits.forEach(unit => {
      if (unit.documentosIrregulares.length === 0) {
        detalheRows.push({
          'Unidade': unit.UnitName || '', 'CNPJ': formatCNPJ(unit.cnpj), 'Endereço': unit.endereco || '',
          'Regional': unit.RegionalName || '', 'UF': unit.UF || '', 'Documento': '-', 'Tipo': '-',
          'Identificação Doc': '-', 'Categoria': '-', 'Status Documento': '-', 'Motivo (Pendência)': '-',
          'Fornecedor Doc': '-', 'Vencimento': '-',
        });
      } else {
        unit.documentosIrregulares.forEach(d => {
          detalheRows.push({
            'Unidade': unit.UnitName || '', 'CNPJ': formatCNPJ(unit.cnpj), 'Endereço': unit.endereco || '',
            'Regional': unit.RegionalName || '', 'UF': unit.UF || '', 'Documento': d.tipoCompleto || d.tipo,
            'Tipo': d.tipo, 'Identificação Doc': d.docName || '-', 'Categoria': d.categoria,
            'Status Documento': d.status, 'Motivo (Pendência)': d.motivo || '-',
            'Fornecedor Doc': d.fornecedor || '-', 'Vencimento': d.vencimento || '-',
          });
        });
      }
    });

    return [
      { key: 'resumo', label: '📊 Resumo', color: 'text-blue-700', bg: 'bg-blue-100 border-blue-400', headers: RESUMO_HEADERS, rows: resumoRows },
      { key: 'detalhes', label: '📋 Pendências Detalhadas', color: 'text-orange-700', bg: 'bg-orange-100 border-orange-400', headers: DETALHE_HEADERS, rows: detalheRows },
    ];
  }, [filteredUnits]);

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-500" />
            Pendências de Regularização
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Acompanhamento de Alvará de Funcionamento e AVCB por unidade
          </p>
        </div>
      </div>

      {/* Separador + Botões de Filtro por Tipo de Documento */}
      <div className="flex items-center gap-4 pb-2 border-b">
        <span className="text-sm font-medium text-muted-foreground">Filtrar por:</span>
        <div className="flex gap-2">
          <Button
            variant={docTypeFilter === 'alvara' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setDocTypeFilter('alvara');
              setKpiFilter('all');
            }}
          >
            Alvará de Funcionamento
          </Button>
          <Button
            variant={docTypeFilter === 'avcb' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setDocTypeFilter('avcb');
              setKpiFilter('all');
            }}
          >
            AVCB
          </Button>
        </div>
        {(kpiFilter !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setKpiFilter('all')}
            className="text-muted-foreground"
          >
            Limpar filtro KPI
          </Button>
        )}
      </div>

      {/* KPIs - Dinâmicos baseado no filtro de documento */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard
          title="Total Unidades"
          value={data?.kpis.totalUnidades || 0}
          icon={<Building2 className="h-5 w-5 text-muted-foreground" />}
          isLoading={isLoading}
          onClick={() => setKpiFilter('all')}
          isActive={kpiFilter === 'all'}
        />
        <KpiCard
          title="Regulares"
          value={
            docTypeFilter === 'alvara' 
              ? (data?.kpis.alvara?.regulares || 0)
              : (data?.kpis.avcb?.regulares || 0)
          }
          icon={<CheckCircle className="h-5 w-5 text-emerald-500" />}
          color="green"
          isLoading={isLoading}
          onClick={() => setKpiFilter('regular')}
          isActive={kpiFilter === 'regular'}
        />
        <KpiCard
          title="Em Andamento"
          value={
            docTypeFilter === 'alvara'
              ? (data?.kpis.alvara?.emAndamento || 0)
              : (data?.kpis.avcb?.emAndamento || 0)
          }
          icon={<Clock className="h-5 w-5 text-yellow-500" />}
          color="yellow"
          isLoading={isLoading}
          onClick={() => setKpiFilter('em_andamento')}
          isActive={kpiFilter === 'em_andamento'}
        />
        <KpiCard
          title="Irregulares"
          value={
            docTypeFilter === 'alvara'
              ? (data?.kpis.alvara?.irregular || 0)
              : (data?.kpis.avcb?.irregular || 0)
          }
          icon={<AlertTriangle className="h-5 w-5 text-red-500" />}
          color="red"
          isLoading={isLoading}
          onClick={() => setKpiFilter('irregular')}
          isActive={kpiFilter === 'irregular'}
        />
        <KpiCard
          title="Total Pendentes"
          value={
            docTypeFilter === 'alvara'
              ? (data?.kpis.alvara?.pendentes || 0)
              : (data?.kpis.avcb?.pendentes || 0)
          }
          icon={<AlertCircle className="h-5 w-5 text-red-500" />}
          color="red"
          isLoading={isLoading}
          onClick={() => setKpiFilter('pendente')}
          isActive={kpiFilter === 'pendente'}
          subtitle="Em Andamento + Irregulares"
        />
      </div>

      {/* Filtros e Tabela */}
      <div className="bg-card rounded-lg border">
        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-3 p-4 border-b">
          <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por unidade, cidade, motivo ou fornecedor..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-9"
            />
          </div>
          
          <Select value={selectedRegional} onValueChange={setSelectedRegional}>
            <SelectTrigger className="w-[180px] h-9">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Regional" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas Regionais</SelectItem>
              {regionals?.map(r => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <MultiSelect
            options={[
              { value: 'em_andamento', label: 'Em Andamento' },
              { value: 'irregular', label: 'Irregular' },
              { value: 'sem_doc', label: 'Sem Documento' },
            ]}
            selected={selectedStatus}
            onChange={setSelectedStatus}
            placeholder="Todos Status"
            className="w-[180px]"
          />

          <MultiSelect
            options={fornecedores.map(f => ({ value: f, label: f }))}
            selected={selectedFornecedor}
            onChange={setSelectedFornecedor}
            placeholder="Todos Fornecedores"
            className="w-[200px]"
          />

          <Button variant="outline" size="sm" onClick={() => setShowPreview(true)} disabled={!filteredUnits.length}>
            <Eye className="h-4 w-4 mr-2" />
            Pré-visualizar
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportXLSX} disabled={!filteredUnits.length}>
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>

          <span className="text-sm text-muted-foreground ml-auto">
            {filteredUnits.length > pageSize 
              ? `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, filteredUnits.length)} de ${filteredUnits.length}`
              : `${filteredUnits.length} unidades`
            }
          </span>
        </div>

        {/* Tabela */}
        <TooltipProvider>
        <div className="max-h-[600px] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead>Unidade</TableHead>
                <TableHead>CNPJ</TableHead>
                <TableHead>Regional</TableHead>
                <TableHead>UF</TableHead>
                <TableHead>Documentos</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Fornecedor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[80px]">Histórico</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><div className="h-4 bg-muted rounded w-48 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded w-36 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded w-16 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded w-8 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded w-24 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded w-40 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-muted rounded w-32 animate-pulse" /></TableCell>
                    <TableCell><div className="h-6 bg-muted rounded w-24 animate-pulse" /></TableCell>
                    <TableCell><div className="h-6 bg-muted rounded w-8 animate-pulse" /></TableCell>
                  </TableRow>
                ))
              ) : filteredUnits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {kpiFilter === 'regular' ? (
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle className="h-12 w-12 text-emerald-500" />
                        <span className="text-lg font-medium">Unidades Regulares</span>
                        <span>
                          Esta página mostra apenas unidades pendentes. 
                          {docTypeFilter === 'alvara' && ` Existem ${data?.kpis.alvara?.regulares || 0} unidades regulares em Alvará.`}
                          {docTypeFilter === 'avcb' && ` Existem ${data?.kpis.avcb?.regulares || 0} unidades regulares em AVCB.`}
                        </span>
                      </div>
                    ) : data?.units.length === 0 ? (
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle className="h-12 w-12 text-emerald-500" />
                        <span className="text-lg font-medium">Parabéns! 🎉</span>
                        <span>Todas as unidades estão regulares em Alvará e AVCB</span>
                      </div>
                    ) : (
                      'Nenhuma unidade encontrada com os filtros aplicados'
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUnits.map((unit) => (
                  <TableRow key={unit.UnitGuid}>
                    <TableCell className="font-medium max-w-[200px] truncate" title={unit.UnitName}>{unit.UnitName}</TableCell>
                    <TableCell className="text-xs font-mono whitespace-nowrap">{formatCNPJ(unit.cnpj)}</TableCell>
                    <TableCell>{unit.RegionalName || '-'}</TableCell>
                    <TableCell>{unit.UF || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <DocStatusBadge hasDoc={unit.temAlvara} docType="Alvará" documentosIrregulares={unit.documentosIrregulares} />
                        <DocStatusBadge hasDoc={unit.temAvcb} docType="AVCB" documentosIrregulares={unit.documentosIrregulares} />
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[250px]">
                      {(() => {
                        const totalDocs = unit.documentosIrregulares.length;
                        const motivos = unit.documentosIrregulares
                          .filter(d => d.motivo)
                          .map(d => d.motivo!);
                        const motivosUnicos = [...new Set(motivos)];
                        const motivoDisplay = motivosUnicos.length > 0 
                          ? motivosUnicos[0]
                          : unit.motivoResumo;
                        const extra = totalDocs > 1 ? ` (+${totalDocs - 1})` : '';
                        
                        return (
                          <button
                            type="button"
                            onClick={() => setDetailUnit(unit)}
                            className="flex items-center gap-1.5 text-left w-full group hover:text-primary transition-colors"
                          >
                            <Eye className="h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-primary" />
                            <span className="truncate text-sm">
                              {motivoDisplay}
                              {extra && <span className="text-muted-foreground font-medium">{extra}</span>}
                            </span>
                          </button>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate" title={unit.fornecedor || ''}>{unit.fornecedor || '-'}</TableCell>
                    <TableCell>
                      {unit.status !== 'regular' && <StatusBadge status={unit.status} />}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewHistory(unit)}
                        title="Ver histórico de mudanças de status"
                      >
                        <History className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        </TooltipProvider>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <span className="text-sm text-muted-foreground">
              Página {currentPage} de {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                Primeira
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Próxima
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Última
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Drawer de Histórico */}
      <Sheet open={historyDrawerOpen} onOpenChange={setHistoryDrawerOpen}>
        <SheetContent className="w-[500px] sm:w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Histórico e Comentários
            </SheetTitle>
          </SheetHeader>
          
          {selectedUnit && (
            <div className="mt-4 space-y-4">
              {/* Info da unidade */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="font-semibold text-lg">{selectedUnit.UnitName}</div>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <span>{selectedUnit.RegionalName || '-'}</span>
                  <span>{selectedUnit.UF || '-'}</span>
                </div>
                <div className="flex gap-2">
                  <DocStatusBadge hasDoc={selectedUnit.temAlvara} docType="Alvará" documentosIrregulares={selectedUnit.documentosIrregulares} />
                  <DocStatusBadge hasDoc={selectedUnit.temAvcb} docType="AVCB" documentosIrregulares={selectedUnit.documentosIrregulares} />
                </div>
{selectedUnit.status !== 'regular' && <StatusBadge status={selectedUnit.status} />}
                
                {/* Documentos irregulares */}
                {selectedUnit.documentosIrregulares.length > 0 && (
                  <div className="mt-2 pt-2 border-t space-y-2">
                    <div className="text-sm font-medium text-destructive">Documentos Irregulares:</div>
                    {selectedUnit.documentosIrregulares.map((doc, idx) => (
                      <div key={idx} className="text-sm p-2 bg-red-50 dark:bg-red-950/30 rounded">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div>
                            <span className="font-medium">{doc.tipo}</span>
                            <span className="mx-2">•</span>
                            <span>{doc.status}</span>
                            {doc.motivo && (
                              <span className="text-muted-foreground ml-2">({doc.motivo})</span>
                            )}
                          </div>
                          {doc.docName && (
                            <Badge variant="secondary" className="text-xs font-normal" title={doc.docName}>
                              {doc.docName}
                            </Badge>
                          )}
                        </div>
                        {doc.vencimento && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Vencimento: {doc.vencimento}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground mt-1">
                          Fornecedor: {doc.fornecedor}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Tabs de Histórico e Comentários */}
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'historico' | 'comentarios')}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="historico" className="flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Histórico
                  </TabsTrigger>
                  <TabsTrigger value="comentarios" className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Comentários
                    {processComments?.data?.length ? (
                      <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                        {processComments.data.length}
                      </Badge>
                    ) : null}
                  </TabsTrigger>
                </TabsList>

                {/* Tab de Histórico */}
                <TabsContent value="historico" className="mt-4">
                  <div className="space-y-2">
                    {loadingHistory ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : !statusHistory?.data?.length ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <History className="h-10 w-10 mx-auto mb-2 opacity-50" />
                        <p>Nenhuma mudança de status encontrada</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {statusHistory.data.map((change: StatusChangeRecord, index: number) => {
                          // Classifica o status usando a mesma lógica do semáforo de documentos
                          const classifyStatus = (status: string): 'Regular' | 'Em Andamento' | 'Irregular' => {
                            if (!status) return 'Irregular';
                            const s = status.toLowerCase().trim();
                            
                            // REGULAR: Isento, Permanente, Vigente, Vigente/Em renovação
                            if (/^(isento|permanente|vigente)$/i.test(s)) return 'Regular';
                            if (/^vigente\s*\/\s*em\s+(renovação|renovacao)$/i.test(s)) return 'Regular';
                            
                            // EM ANDAMENTO: Em obtenção ou renovação ativa
                            if (/em (obtenção|obtencao)|\/em (obtenção|obtencao)/.test(s)) return 'Em Andamento';
                            if (/^vencido\s*\/\s*em\s+(renovação|renovacao)$/i.test(s)) return 'Em Andamento';
                            if (/^inexistente\s*\/\s*em\s+(obtenção|obtencao)$/i.test(s)) return 'Em Andamento';
                            
                            return 'Irregular';
                          };
                          
                          const oldStatusCategory = classifyStatus(change.OldStatusDocumentName || '');
                          const newStatusCategory = classifyStatus(change.NewStatusDocumentName || '');
                          
                          // Perda de vigência = era Regular e agora não é mais
                          const isCriticalChange = oldStatusCategory === 'Regular' && newStatusCategory !== 'Regular';
                          // Ganho de vigência = não era Regular e agora é
                          const isPositiveChange = oldStatusCategory !== 'Regular' && newStatusCategory === 'Regular';
                          
                          // Formata a data
                          const dateFormatted = new Date(change.DateHistorical).toLocaleDateString('pt-BR', {
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          });
                          
                          // Cores baseadas na categoria
                          const getStatusColor = (category: string) => {
                            switch (category) {
                              case 'Regular': return 'text-emerald-600 dark:text-emerald-400';
                              case 'Em Andamento': return 'text-yellow-600 dark:text-yellow-400';
                              default: return 'text-red-600 dark:text-red-400';
                            }
                          };
                          
                          return (
                            <div
                              key={index}
                              className={cn(
                                "border rounded-lg p-3 space-y-2",
                                isCriticalChange 
                                  ? "bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-800" 
                                  : isPositiveChange
                                    ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800"
                                    : "bg-card"
                              )}
                            >
                              <div className="flex items-center justify-between text-sm">
                                <span className="font-medium">{dateFormatted}</span>
                                {isCriticalChange && (
                                  <Badge variant="destructive" className="text-xs">
                                    Perda de Vigência
                                  </Badge>
                                )}
                                {isPositiveChange && (
                                  <Badge className="text-xs bg-emerald-500">
                                    Regularizado
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm font-medium">{change.DocumentTypeName}</div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className={getStatusColor(oldStatusCategory)}>
                                  {change.OldStatusDocumentName || '-'}
                                </span>
                                <span className="text-muted-foreground">→</span>
                                <span className={cn(getStatusColor(newStatusCategory), isCriticalChange && 'font-medium')}>
                                  {change.NewStatusDocumentName || '-'}
                                </span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                por {change.UserHistoricalName}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Tab de Comentários */}
                <TabsContent value="comentarios" className="mt-4">
                  <div className="space-y-4">
                    {/* Seletor de tipo de documento */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Documento:</span>
                      <Select 
                        value={selectedDocType} 
                        onValueChange={(v) => setSelectedDocType(v as 'alvara' | 'avcb')}
                      >
                        <SelectTrigger className="w-[220px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alvara" disabled={processGuidsByDocType.alvara.length === 0}>
                            <div className="flex items-center gap-2">
                              <span>Alvará de Funcionamento</span>
                              {processGuidsByDocType.alvara.length === 0 && (
                                <span className="text-xs text-muted-foreground">(sem processo)</span>
                              )}
                              {processGuidsByDocType.alvara.length > 1 && (
                                <span className="text-xs text-muted-foreground">({processGuidsByDocType.alvara.length} docs)</span>
                              )}
                            </div>
                          </SelectItem>
                          <SelectItem value="avcb" disabled={processGuidsByDocType.avcb.length === 0}>
                            <div className="flex items-center gap-2">
                              <span>AVCB</span>
                              {processGuidsByDocType.avcb.length === 0 && (
                                <span className="text-xs text-muted-foreground">(sem processo)</span>
                              )}
                              {processGuidsByDocType.avcb.length > 1 && (
                                <span className="text-xs text-muted-foreground">({processGuidsByDocType.avcb.length} docs)</span>
                              )}
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Conteúdo dos comentários */}
                    {activeProcessGuids.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-50" />
                        <p>Nenhum processo ativo vinculado</p>
                        <p className="text-xs mt-1">Selecione um documento com processo ativo</p>
                      </div>
                    ) : loadingComments ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : !processComments?.data?.length ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-50" />
                        <p>Nenhum comentário encontrado</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {processComments.data.map((comment: ProcessCommentWithSource) => {
                          // Formata a data
                          const dateFormatted = new Date(comment.DateRegistration).toLocaleDateString('pt-BR', {
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          });
                          
                          // Obtém o nome do documento de onde veio o comentário
                          const docName = selectedUnit?.processGuidToDocName?.[comment.sourceProcessGuid];
                          
                          return (
                            <div
                              key={comment.Guid}
                              className={cn(
                                "border rounded-lg p-3 space-y-2 bg-card",
                                comment.Private && "border-yellow-300 bg-yellow-50/50 dark:bg-yellow-950/20"
                              )}
                            >
                              <div className="flex items-center justify-between text-sm">
                                <span className="font-medium">{dateFormatted}</span>
                                <div className="flex items-center gap-2">
                                  {docName && (
                                    <Badge variant="secondary" className="text-xs font-normal max-w-[200px] truncate" title={docName}>
                                      {docName}
                                    </Badge>
                                  )}
                                  {comment.Private && (
                                    <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-600">
                                      Privado
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div 
                                className="text-sm whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{ 
                                  __html: comment.TextComment.replace(/<br\s*\/?>/gi, '<br />') 
                                }}
                              />
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>por {comment.UserRegistrationName}</span>
                                {comment.ProcessFlowStepName && (
                                  <>
                                    <span>•</span>
                                    <span>Etapa: {comment.ProcessFlowStepName}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Dialog de detalhes das pendências */}
      <Dialog open={!!detailUnit} onOpenChange={(open) => !open && setDetailUnit(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Pendências — {detailUnit?.UnitName}
            </DialogTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground pt-1">
              {detailUnit?.cnpj && <span>CNPJ: {formatCNPJ(detailUnit.cnpj)}</span>}
              {detailUnit?.RegionalName && <><span>•</span><span>{detailUnit.RegionalName}</span></>}
              {detailUnit?.UF && <><span>•</span><span>{detailUnit.UF}</span></>}
            </div>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            {detailUnit && detailUnit.documentosIrregulares.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Nenhuma pendência detalhada.</p>
            ) : (
              <div className="space-y-3">
                {detailUnit?.documentosIrregulares.map((doc, idx) => (
                  <div key={idx} className={cn(
                    "border rounded-lg p-3 space-y-1.5",
                    doc.categoria === 'Irregular'
                      ? 'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20'
                      : 'border-yellow-200 bg-yellow-50/50 dark:border-yellow-800 dark:bg-yellow-950/20'
                  )}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={cn(
                          "text-xs",
                          doc.categoria === 'Irregular'
                            ? 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900 dark:text-red-300'
                            : 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900 dark:text-yellow-300'
                        )}>
                          {doc.categoria}
                        </Badge>
                        <span className="font-medium text-sm">{doc.tipoCompleto || doc.tipo}</span>
                      </div>
                      {doc.docName && (
                        <Badge variant="secondary" className="text-xs font-mono">{doc.docName}</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div><span className="text-muted-foreground">Status:</span> {doc.status}</div>
                      <div><span className="text-muted-foreground">Motivo:</span> {doc.motivo || '-'}</div>
                      <div><span className="text-muted-foreground">Fornecedor:</span> {doc.fornecedor || '-'}</div>
                      <div><span className="text-muted-foreground">Vencimento:</span> {doc.vencimento || '-'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center pt-3 border-t text-sm text-muted-foreground">
            <span>Total: {detailUnit?.documentosIrregulares.length || 0} pendência(s)</span>
            <Button variant="outline" size="sm" onClick={() => setDetailUnit(null)}>Fechar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Excel Preview */}
      <ExcelPreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        sheets={previewSheets}
        filename={`pendencias-alvara-${new Date().toISOString().split('T')[0]}.xlsx`}
        onDownload={handleExportXLSX}
      />
    </div>
  );
}
